import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { MetadataProvider } from "./context/MetadataContext";
import { identifyUser } from "./utils/analytics";
import ErrorBoundary, { RouteErrorBoundary } from "./components/ErrorBoundary";
import PageTracker from "./components/PageTracker";
import Layout from "./components/Layout";
import Home from "./pages/Home";
import Projects from "./pages/Projects";
import ProjectDetail from "./pages/ProjectDetail";
import Deals from "./pages/Deals";
import DealDetail from "./pages/DealDetail";
import Valuations from "./pages/Valuations";
import ValuationDetail from "./pages/ValuationDetail";
import Automations from "./pages/Automations";
import AutomationDetail from "./pages/AutomationDetail";
import NewProject from "./pages/NewProject";
import NewDeal from "./pages/NewDeal";
import NewValuation from "./pages/NewValuation";

// Reports pages
import Overview from "./pages/reports/Overview";
import Performance from "./pages/reports/Performance";
import Trends from "./pages/reports/Trends";

// Documents pages
import Templates from "./pages/documents/Templates";
import Reports from "./pages/documents/Reports";
import SharedFiles from "./pages/documents/Shared";

// User management pages
import Users from "./pages/users/Users";
import Roles from "./pages/users/Roles";
import Teams from "./pages/users/Teams";

// Settings pages
import General from "./pages/settings/General";
import Integrations from "./pages/settings/Integrations";
import Security from "./pages/settings/Security";
import Backup from "./pages/settings/Backup";

function App() {
  // Identify user once when app loads
  useEffect(() => {
    identifyUser();
  }, []);

  return (
    <ErrorBoundary>
      <MetadataProvider enablePreload={true} enableAutoRefresh={false}>
        <Router>
          <PageTracker />
          <Routes>
            <Route path="/" element={<Layout />}>
              <Route
                index
                element={
                  <RouteErrorBoundary routeName="Home">
                    <Home />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="projects"
                element={
                  <RouteErrorBoundary routeName="Projects">
                    <Projects />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="new-project"
                element={
                  <RouteErrorBoundary routeName="New Project">
                    <NewProject />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="project/:id"
                element={
                  <RouteErrorBoundary routeName="Project Details">
                    <ProjectDetail />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="deals"
                element={
                  <RouteErrorBoundary routeName="Deals">
                    <Deals />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="deal/:id"
                element={
                  <RouteErrorBoundary routeName="Deal Details">
                    <DealDetail />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="valuations"
                element={
                  <RouteErrorBoundary routeName="Valuations">
                    <Valuations />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="valuation/:id"
                element={
                  <RouteErrorBoundary routeName="Valuation Details">
                    <ValuationDetail />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="automations"
                element={
                  <RouteErrorBoundary routeName="Automations">
                    <Automations />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="automation/:id"
                element={
                  <RouteErrorBoundary routeName="Automation Details">
                    <AutomationDetail />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="new-deal"
                element={
                  <RouteErrorBoundary routeName="New Deal">
                    <NewDeal />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="new-valuation"
                element={
                  <RouteErrorBoundary routeName="New Valuation">
                    <NewValuation />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="new-automation"
                element={
                  <RouteErrorBoundary routeName="New Automation">
                    <Automations />
                  </RouteErrorBoundary>
                }
              />

              {/* Reports Routes */}
              <Route
                path="reports/overview"
                element={
                  <RouteErrorBoundary routeName="Analytics Overview">
                    <Overview />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="reports/performance"
                element={
                  <RouteErrorBoundary routeName="Performance Analytics">
                    <Performance />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="reports/trends"
                element={
                  <RouteErrorBoundary routeName="Market Trends">
                    <Trends />
                  </RouteErrorBoundary>
                }
              />

              {/* Documents Routes */}
              <Route
                path="documents/templates"
                element={
                  <RouteErrorBoundary routeName="Document Templates">
                    <Templates />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="documents/reports"
                element={
                  <RouteErrorBoundary routeName="Generated Reports">
                    <Reports />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="documents/shared"
                element={
                  <RouteErrorBoundary routeName="Shared Files">
                    <SharedFiles />
                  </RouteErrorBoundary>
                }
              />

              {/* User Management Routes */}
              <Route
                path="users"
                element={
                  <RouteErrorBoundary routeName="User Management">
                    <Users />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="users/roles"
                element={
                  <RouteErrorBoundary routeName="Roles & Permissions">
                    <Roles />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="users/teams"
                element={
                  <RouteErrorBoundary routeName="Team Settings">
                    <Teams />
                  </RouteErrorBoundary>
                }
              />

              {/* Settings Routes */}
              <Route
                path="settings/general"
                element={
                  <RouteErrorBoundary routeName="General Settings">
                    <General />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="settings/integrations"
                element={
                  <RouteErrorBoundary routeName="Integrations">
                    <Integrations />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="settings/security"
                element={
                  <RouteErrorBoundary routeName="Security Settings">
                    <Security />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="settings/backup"
                element={
                  <RouteErrorBoundary routeName="Backup Settings">
                    <Backup />
                  </RouteErrorBoundary>
                }
              />
            </Route>
          </Routes>
        </Router>
      </MetadataProvider>
    </ErrorBoundary>
  );
}

export default App;
